import React from "react";

import type {
  CheckpointStatus,
  ProcessingDecision,
  ProcessingJobState,
  SongType,
} from "./processingTypes";

// Slice #6 / UI-0 — shared presentational helpers (badges, colors, dates) used
// by both the Processing Jobs list and the Job detail page. Color semantics:
//   pass/approved      → green
//   fail/not_approved  → red
//   awaiting_review/warning → amber
//   pending/in-progress → blue/grey

type BadgeTone = "green" | "red" | "amber" | "orange" | "blue" | "grey" | "dark";

const TONE_STYLE: Record<BadgeTone, React.CSSProperties> = {
  green: { background: "#d1e7dd", color: "#0f5132" },
  red: { background: "#f8d7da", color: "#842029" },
  amber: { background: "#fff3cd", color: "#664d03" },
  orange: { background: "#ffe5d0", color: "#8a3c00" },
  blue: { background: "#cfe2ff", color: "#084298" },
  grey: { background: "#e2e3e5", color: "#41464b" },
  dark: { background: "#343a40", color: "#f8f9fa" },
};

function badge(label: string, tone: BadgeTone, compact = false): React.ReactElement {
  return (
    <span
      style={{
        display: "inline-block",
        padding: compact ? "1px 8px" : "2px 10px",
        borderRadius: 999,
        fontSize: compact ? 11 : 12,
        fontWeight: 600,
        lineHeight: compact ? 1.25 : 1.35,
        whiteSpace: "nowrap",
        ...TONE_STYLE[tone],
      }}
    >
      {label}
    </span>
  );
}

function humanize(value: string): string {
  return value
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

const STATE_TONE: Record<ProcessingJobState, BadgeTone> = {
  upload_initiated: "grey",
  upload_received: "blue",
  queued: "grey",
  awaiting_processing: "blue",
  upload_expired: "red",
  validating: "blue",
  scanning: "blue",
  enriching: "blue",
  awaiting_review: "amber",
  linking: "blue",
  transcoding: "blue",
  finalizing: "blue",
  approved: "green",
  not_approved: "red",
  failed: "red",
};

export function renderStateBadge(
  state: ProcessingJobState | undefined,
  compact = false,
): React.ReactElement {
  if (!state) return badge("—", "grey", compact);
  const tone = STATE_TONE[state] ?? "grey";
  return badge(humanize(state), tone, compact);
}

const DECISION_TONE: Record<ProcessingDecision, BadgeTone> = {
  approved: "green",
  not_approved: "red",
  failed: "red",
};

export function renderDecisionBadge(
  decision: ProcessingDecision | undefined,
): React.ReactElement {
  if (!decision) return <span>—</span>;
  return badge(humanize(decision), DECISION_TONE[decision] ?? "grey");
}

// Operator Guidance (owner 2026-07-19) — ONE overall "Status" answering the
// three questions an operator always has: what is happening, why, and what do
// I do next. Every non-terminal job resolves to one of a handful of SITUATIONS
// with its own experience — never a single undifferentiated "Processing":
//   processing      → the pipeline is currently executing (recent activity)
//   waiting_review  → parked; OPERATOR action required (metadata review)
//   waiting_catalog → parked; waiting on catalog records (platform dependency)
//   stalled         → in-flight but no activity past the stall threshold —
//                     likely interrupted by a deployment/infrastructure event
//   failed / not_approved / approved / upload_expired / archived → terminal.
// The underlying state/decision fields are untouched and stay on the Job
// Detail (Technical Details) for auditing.
export type JobOverallStatus =
  | "archived"
  | "approved"
  | "not_approved"
  | "failed"
  | "waiting_review"
  | "waiting_catalog"
  | "upload_expired"
  | "stalled"
  | "processing";

/** Mirrors the API's STALL_THRESHOLD_MS (lib/archive-job.js): an in-flight job
 * with no canonical write for this long will not advance on its own. Every
 * pipeline stage completes in well under a minute, so 30 minutes is far beyond
 * any legitimate gap. The API accepts Archive for these jobs. */
export const STALL_THRESHOLD_MS = 30 * 60 * 1000;

/** Catalog-linking park (P3c): the job is waiting on catalog RECORDS, not on
 * a metadata review — a different situation with different next steps. */
export const CATALOG_UNRESOLVED_REASON = "catalog-link-unresolved";

export interface JobStatusInput {
  state: ProcessingJobState;
  decision?: ProcessingDecision;
  archived?: boolean;
  reasonCode?: string | null;
  updatedAt?: string | null;
  createdAt?: string | null;
  /** DRA-3 — when set, failed replace jobs use a distinct operator label. */
  jobPurpose?: string | null;
}

export function isJobStalled(row: JobStatusInput, nowMs: number = Date.now()): boolean {
  if (row.archived === true) return false;
  const inFlight =
    row.state !== "approved" &&
    row.state !== "not_approved" &&
    row.state !== "failed" &&
    row.state !== "upload_expired" &&
    row.state !== "awaiting_review" &&
    !row.decision;
  if (!inFlight) return false;
  const ts = Date.parse(row.updatedAt || row.createdAt || "");
  return Number.isFinite(ts) && nowMs - ts > STALL_THRESHOLD_MS;
}

export function deriveJobStatus(
  row: JobStatusInput,
  nowMs: number = Date.now(),
): JobOverallStatus {
  // Archive is orthogonal to pipeline state (PJL invariant) but IS the
  // job's overall operational status once set.
  if (row.archived === true) return "archived";
  if (row.decision === "approved") return "approved";
  if (row.decision === "not_approved") return "not_approved";
  if (row.decision === "failed") return "failed";
  if (row.state === "awaiting_review") {
    return row.reasonCode === CATALOG_UNRESOLVED_REASON
      ? "waiting_catalog"
      : "waiting_review";
  }
  if (row.state === "upload_expired") return "upload_expired";
  if (isJobStalled(row, nowMs)) return "stalled";
  return "processing";
}

const JOB_STATUS_DISPLAY: Record<JobOverallStatus, { label: string; tone: BadgeTone }> = {
  archived: { label: "Archived", tone: "dark" },
  approved: { label: "Approved", tone: "green" },
  not_approved: { label: "Not Approved", tone: "red" },
  failed: { label: "Failed", tone: "red" },
  waiting_review: { label: "Waiting for Review", tone: "amber" },
  waiting_catalog: { label: "Waiting for Catalog Records", tone: "orange" },
  upload_expired: { label: "Upload Expired", tone: "grey" },
  stalled: { label: "Stalled", tone: "orange" },
  processing: { label: "Processing", tone: "blue" },
};

export function renderJobStatusBadge(row: JobStatusInput): React.ReactElement {
  const status = deriveJobStatus(row);
  const isReplaceFailure =
    row.jobPurpose === "replace-song-audio" &&
    (status === "failed" || row.state === "failed" || row.decision === "failed");
  const { label, tone } = isReplaceFailure
    ? { label: "Song Audio Replacement Failed", tone: "red" as BadgeTone }
    : JOB_STATUS_DISPLAY[status];
  if (status === "stalled" && !isReplaceFailure) {
    return (
      <span style={{ whiteSpace: "nowrap" }}>
        <i
          className="bi bi-exclamation-triangle-fill"
          style={{ color: "#8a3c00", marginRight: 4, fontSize: 12 }}
          aria-hidden="true"
        />
        {badge(label, tone)}
      </span>
    );
  }
  return badge(label, tone);
}

/** "17 days" / "3 hours" / "12 minutes" — how long a job has sat with no
 * pipeline activity. Used by the Stalled guidance ("No activity for: …"). */
export function formatDurationSince(
  isoTimestamp: string | null | undefined,
  nowMs: number = Date.now(),
): string {
  const ts = Date.parse(isoTimestamp || "");
  if (!Number.isFinite(ts)) return "—";
  const ms = Math.max(0, nowMs - ts);
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return "less than a minute";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} hour${hours === 1 ? "" : "s"}`;
  const days = Math.floor(hours / 24);
  return `${days} days`;
}

export function renderSongTypeBadge(
  songType: SongType | undefined,
  compact = false,
): React.ReactElement {
  if (songType === "kk-original") return badge("KK Original", "blue", compact);
  if (songType === "commercial") return badge("Commercial", "grey", compact);
  return <span>—</span>;
}

export function renderChecksumBadge(
  status: string | null | undefined,
  compact = false,
): React.ReactElement {
  if (status === "computed") return badge("Computed", "green", compact);
  if (status === "failed") return badge("Failed", "red", compact);
  if (status === "pending") return badge("Pending", "grey", compact);
  return <span>—</span>;
}

/**
 * Display-only checkpoint status. `in_progress` is derived client-side: the
 * API keeps status "pending" while a step runs, but a step with `startedAt`
 * and no `completedAt` is actively executing and should not read as idle.
 */
export type DisplayCheckpointStatus = CheckpointStatus | "in_progress";

export function deriveDisplayCheckpointStatus(checkpoint: {
  status: CheckpointStatus;
  startedAt?: string | null;
  completedAt?: string | null;
} | undefined): DisplayCheckpointStatus {
  if (!checkpoint) return "pending";
  if (
    checkpoint.status === "pending" &&
    checkpoint.startedAt &&
    !checkpoint.completedAt
  ) {
    return "in_progress";
  }
  return checkpoint.status;
}

const CHECKPOINT_TONE: Record<DisplayCheckpointStatus, BadgeTone> = {
  pass: "green",
  fail: "red",
  warning: "amber",
  pending: "grey",
  skipped: "grey",
  in_progress: "blue",
};

const CHECKPOINT_ICON: Record<DisplayCheckpointStatus, string> = {
  pass: "bi-check-circle-fill",
  fail: "bi-x-circle-fill",
  warning: "bi-exclamation-triangle-fill",
  pending: "bi-clock",
  skipped: "bi-dash-circle",
  in_progress: "bi-arrow-repeat",
};

const CHECKPOINT_ICON_COLOR: Record<DisplayCheckpointStatus, string> = {
  pass: "#0f5132",
  fail: "#842029",
  warning: "#997404",
  pending: "#6c757d",
  skipped: "#adb5bd",
  in_progress: "#084298",
};

export function renderCheckpointStatusBadge(
  status: DisplayCheckpointStatus,
  options?: { labelOverride?: string },
): React.ReactElement {
  const label = options?.labelOverride ?? humanize(status);
  return badge(label, CHECKPOINT_TONE[status] ?? "grey");
}

/**
 * Operator-facing label for a checkpoint. The policy-check gate is an
 * intentional bypass (Content Safety future phase) — never present it as a
 * quiet "Skipped". Prefer the structured reasonCode / detail markers written
 * by finalize; fall back to the raw status for every other checkpoint.
 */
export function checkpointStatusLabel(
  key: string | undefined,
  status: DisplayCheckpointStatus,
  checkpoint?: { reasonCode?: string | null; detail?: string | null } | null,
): string {
  const isPolicyBypass =
    key === "policy-check" &&
    status === "skipped" &&
    (checkpoint?.reasonCode === "policy-gate-not-implemented" ||
      (typeof checkpoint?.detail === "string" &&
        (/bypassed/i.test(checkpoint.detail) ||
          /not implemented/i.test(checkpoint.detail))));
  if (isPolicyBypass) {
    return "Bypassed — Policy Gate Not Yet Implemented";
  }
  return humanize(status);
}

export function checkpointIconClass(status: DisplayCheckpointStatus): string {
  return CHECKPOINT_ICON[status] ?? "bi-clock";
}

export function checkpointIconColor(status: DisplayCheckpointStatus): string {
  return CHECKPOINT_ICON_COLOR[status] ?? "#6c757d";
}

/** Ant Design Steps status mapping for the timeline. */
export function checkpointStepStatus(
  status: DisplayCheckpointStatus,
): "finish" | "error" | "process" | "wait" {
  switch (status) {
    case "pass":
      return "finish";
    case "fail":
      return "error";
    case "warning":
      return "process";
    case "skipped":
      return "finish";
    case "in_progress":
      return "process";
    default:
      return "wait";
  }
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.slice(0, 19) || "—";
  return date.toLocaleString();
}
